import { Injectable } from '@angular/core';
import { User } from '../../models/identity/user.model';
import { Message } from '../type/message.type';

@Injectable({ providedIn: 'root' })
export class ChatNotificationService {
    private audio: HTMLAudioElement;
    private notificationPermission: NotificationPermission = 'denied';

    constructor() {
        this.audio = new Audio('assets/sounds/notification.mp3');
        this.audio.preload = 'auto';
        this.initializePermission();
    }

    /**
     * Initialize notification permission from browser state
     */
    private initializePermission(): void {
        if ('Notification' in window) {
            this.notificationPermission = Notification.permission;
        }
    }

    /**
     * Request permission for browser notifications
     */
    async requestPermission(): Promise<NotificationPermission> {
        if (!('Notification' in window)) {
            console.warn('Browser notifications not supported');
            return 'denied';
        }

        if (this.notificationPermission === 'granted') {
            return 'granted';
        }

        if (this.notificationPermission === 'denied') {
            return 'denied';
        }

        try {
            this.notificationPermission = await Notification.requestPermission();
            return this.notificationPermission;
        } catch (error) {
            console.error('Failed to request notification permission:', error);
            return 'denied';
        }
    }

    /**
     * Check if notification permission is granted
     */
    get isPermissionGranted(): boolean {
        return this.notificationPermission === 'granted';
    }

    /**
     * Check if browser tab is hidden
     */
    get isTabHidden(): boolean {
        return document.hidden;
    }

    playNotificationSound(): void {
        // Rewind and play
        this.audio.currentTime = 0;
        this.audio.play().catch(() => {
            // Ignore play errors (e.g user gesture required)
        });
    }

    /**
     * Show browser notification with sender info and message preview
     * Only displays if tab is hidden and permission is granted
     */
    showBrowserNotification(sender: User, message: Message, onClick: () => void): void {
        // Only show notification if tab is hidden and permission is granted
        if (!this.isTabHidden || !this.isPermissionGranted) {
            return;
        }

        try {
            const senderName = `${sender.firstName} ${sender.lastName}`.trim() || 'Unknown User';
            const preview = this.truncateMessage(message.content, 50);
            const options: NotificationOptions = {
                body: preview,
                icon: sender.pictureReference || sender.profilePicture || 'assets/default-avatar.png',
                badge: 'assets/icon-192x192.png',
                tag: `chat-${message.conversationId}`,
                requireInteraction: false,
            };

            const notification = new Notification(senderName, options);

            notification.addEventListener('click', () => {
                onClick();
                notification.close();
            });
        } catch (error) {
            console.error('Failed to show notification:', error);
        }
    }

    /**
     * Truncate message to specified length with ellipsis
     */
    private truncateMessage(content: string, maxLength: number): string {
        if (content.length <= maxLength) {
            return content;
        }
        return content.substring(0, maxLength) + '...';
    }
}
